import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Card,
  Switch,
  Table,
  Tag,
  Typography,
  message,
  Skeleton,
} from 'antd';
import {
  fetchMerchantPaymentMethods,
  updateMerchantPaymentMethods,
  paymentMethodTypeLabel,
  type MerchantPaymentMethodItem,
} from '../../services/merchantPaymentMethodsService';

interface PaymentMethodsTabProps {
  restaurantId?: number;
}

export const PaymentMethodsTab: React.FC<PaymentMethodsTabProps> = ({
  restaurantId,
}) => {
  const [items, setItems] = useState<MerchantPaymentMethodItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const data = await fetchMerchantPaymentMethods(restaurantId);
      setItems(data);
    } catch {
      message.error('Không tải được phương thức thanh toán');
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggleAvailable = async (
    row: MerchantPaymentMethodItem,
    checked: boolean
  ) => {
    if (!restaurantId) return;
    setSavingId(row.paymentMethodId);
    try {
      const updated = await updateMerchantPaymentMethods(restaurantId, [
        { paymentMethodId: row.paymentMethodId, isAvailable: checked },
      ]);
      setItems(updated);
      message.success(
        checked ? 'Đã bật AVAILABLE cho quán' : 'Đã tắt AVAILABLE'
      );
    } catch {
      message.error('Cập nhật thất bại');
      load();
    } finally {
      setSavingId(null);
    }
  };

  if (!restaurantId) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Chưa có restaurant ID cho merchant này."
      />
    );
  }

  return (
    <div className="merchant-detail-payment-methods-tab">
      <Card
        className="merchant-detail-info-card"
        title="Phương thức thanh toán"
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Bật <strong>AVAILABLE</strong> để merchant có thể chọn bật/tắt trên
          app. Chỉ các phương thức đang active trên hệ thống mới hiển thị cho
          khách khi merchant bật.
        </Typography.Paragraph>

        {loading ? (
          <Skeleton active paragraph={{ rows: 5 }} />
        ) : (
          <Table<MerchantPaymentMethodItem>
            rowKey="paymentMethodId"
            dataSource={items}
            pagination={false}
            columns={[
              {
                title: 'Phương thức',
                render: (_, r) => (
                  <div>
                    <div style={{ fontWeight: 600 }}>{r.name}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      {paymentMethodTypeLabel(r.type)}
                    </div>
                  </div>
                ),
              },
              {
                title: 'Hệ thống',
                render: (_, r) => (
                  <Tag color={r.platformIsActive ? 'green' : 'default'}>
                    {r.platformIsActive ? 'Active' : 'Inactive'}
                  </Tag>
                ),
              },
              {
                title: 'Phí',
                render: (_, r) => (r.fee > 0 ? `${r.fee}đ` : '–'),
              },
              {
                title: 'AVAILABLE (Admin)',
                align: 'center',
                render: (_, r) => (
                  <Switch
                    checked={r.isAvailable}
                    disabled={
                      !r.platformIsActive || savingId === r.paymentMethodId
                    }
                    loading={savingId === r.paymentMethodId}
                    onChange={checked => handleToggleAvailable(r, checked)}
                  />
                ),
              },
              {
                title: 'Merchant đang bật',
                align: 'center',
                render: (_, r) =>
                  r.isAvailable ? (
                    <Tag color={r.isEnabledByMerchant ? 'blue' : 'default'}>
                      {r.isEnabledByMerchant ? 'Bật' : 'Tắt'}
                    </Tag>
                  ) : (
                    <span style={{ color: '#9ca3af' }}>–</span>
                  ),
              },
            ]}
          />
        )}
      </Card>
    </div>
  );
};

export default PaymentMethodsTab;
